import React from 'react'

const getTypeStyles = (type) => {
  if (type === 'deposit') {
    return {
      badge: 'bg-white/15 text-white',
      dot: 'bg-customVibrantGreen',
      gradient: 'from-customAccentBlue via-customAccentBlue/80 to-customVibrantGreen',
      amount: 'text-customVibrantGreen',
    }
  }

  return {
    badge: 'bg-white/15 text-white',
    dot: 'bg-customFontRed',
    gradient: 'from-customFontRed via-customOrange to-customAccentBlue',
    amount: 'text-customFontRed',
  }
}

const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number') {
    return amount
  }

  try {
    return amount.toLocaleString(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    })
  } catch {
    return `${currency || 'USD'} ${amount}`
  }
}

const TransactionCard = ({ transaction, onAction }) => {
  const typeStyles = getTypeStyles(transaction?.type)

  return (
    <article className="group relative w-full max-w-xl rounded-2xl border border-customAccentBlue/15 bg-white/95 p-4 shadow-custom transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl dark:border-customSurfaceMuted/60 dark:bg-customSurface">
      <div
        className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${typeStyles.gradient} px-4 py-3 text-white`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70">
              Transaction
            </p>
            <p className="text-base font-semibold leading-tight">{transaction?.transactionNo}</p>
          </div>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${typeStyles.badge}`}
          >
            <span className={`h-2 w-2 rounded-full ${typeStyles.dot}`} aria-hidden="true" />
            {transaction?.type}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-white/80">
          <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
            ID #{transaction?.id}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
            {transaction?.createdDate}
          </span>
        </div>
      </div>

      {/* <p className="mt-3 rounded-xl border border-dashed border-customAccentBlue/15 bg-customSlate/70 px-3 py-2 text-xs text-customSecondaryText dark:border-white/10 dark:bg-white/5 dark:text-customMuted">
        {transaction?.remark}
      </p> */}

      <section className="mt-3 grid gap-3 text-sm text-customPrimaryText dark:text-customHeading">
        <div className="flex items-center gap-3 rounded-xl border border-customAccentBlue/15 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-customSurfaceMuted">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-customAccentBlue/15">
            <img
              src={require(`../../../assets/banks/${transaction?.bankType}.png`)}
              alt={transaction?.bankType || 'bank'}
              className="h-7 w-7 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null
                e.target.src = require('../../../assets/banks/dollar.png')
              }}
            />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-customMuted">
              Account
            </p>
            <p className="truncate text-sm font-semibold leading-tight">
              {transaction?.accountName}
            </p>
            <p className="text-xs text-customSecondaryText dark:text-customMuted">
              {transaction?.accountNo}
            </p>
          </div>
        </div>

        <div className="grid gap-2 rounded-xl border border-customAccentBlue/10 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-customSurfaceMuted">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-customMuted">
              User
            </p>
            <p className="text-sm font-semibold leading-tight">{transaction?.username}</p>
            <p className="text-xs text-customSecondaryText dark:text-customMuted">
              {transaction?.remark}
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-customAccentBlue/10 px-3 py-2 dark:bg-white/5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-customMuted">
              Amount
            </span>
            <span className={`text-base font-bold ${typeStyles.amount}`}>
              {formatAmount(transaction?.amount, transaction?.currency)}
            </span>
          </div>
        </div>
      </section>

      <footer className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => onAction(transaction, 'approve')}
          className="inline-flex w-full items-center justify-center rounded-lg bg-customDarkGreen px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-customDarkGreen/90 focus:outline-none focus:ring-2 focus:ring-customDarkGreen/40"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => onAction(transaction, 'reject')}
          className="inline-flex w-full items-center justify-center rounded-lg bg-customFontRed px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-customFontRed/90 focus:outline-none focus:ring-2 focus:ring-customFontRed/40"
        >
          Reject
        </button>
      </footer>
    </article>
  )
}

const TransactionsCard = ({ data, onAction }) => {
  if (!data || data.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-customPredawn/40 bg-white/95 px-5 py-12 text-center text-sm text-customSecondaryText shadow-inner dark:border-white/10 dark:bg-customSurface">
        <p className="text-base font-semibold text-customPrimaryText dark:text-customHeading">
          All caught up
        </p>
        <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">
          No pending deposits or withdrawals matching your filters.
        </p>
      </div>
    )
  }

  return (
    <div className="grid place-items-center gap-4">
      {data.map((transaction) => (
        <TransactionCard
          key={transaction?.id}
          transaction={transaction}
          onAction={onAction}
        />
      ))}
    </div>
  )
}

export { TransactionCard }
export default TransactionsCard
